// @ts-nocheck
/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Table,
    ScrollArea,
    Stack,
    Flex,
    Text,
    Title,
    HoverCard,
    Badge,
    Loader,
    LoadingOverlay,
} from '@mantine/core';
import dayjs from 'dayjs';
import { IconRefresh, IconSearch } from '@tabler/icons-react';

import getColorByUuid, {
    mantineColorHexCodes,
} from '../../helpers/userColorMap';
import DebouncedInput from '../common/DebouncedInput';
import { getDatesBetween } from '../../helpers/getDatesBetween';
import { useQuery } from '@apollo/client';
import { GET_STAND_IN_GANTT_DATA } from '../../apollo/queries/standInManagement.ts';

type GanttTimeline = {
    day: Date;
    numberOfDays: number;
    color: string;
    isStart: boolean;
    isEnd: boolean;
    substituteFirstName: string;
    substituteLastName: string;
    substituteId: string;
};

function StandInCalendar() {
    const [startDate, setStartDate] = useState(
        dayjs().subtract(1, 'month').startOf('month').toDate(),
    );
    const [endDate, setEndDate] = useState(
        dayjs().add(1, 'month').endOf('month').toDate(),
    );

    const { data: queryData, loading: loadingGantData } = useQuery(
        GET_STAND_IN_GANTT_DATA,
        {
            variables: {
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
            },
        },
    );

    const data = queryData?.getStandInGanttData || [];

    const days = useMemo(() => {
        return getDatesBetween(startDate, endDate);
    }, [startDate, endDate]);

    const scrollRef = useRef<HTMLDivElement>(null);

    const scrollToDate = (date: Date, smooth = true) => {
        const diff = dayjs(date).diff(startDate, 'day');

        scrollRef.current?.scrollTo({
            left: diff * 104 + 57,
            behavior: smooth ? 'smooth' : 'auto',
        });
    };

    useEffect(() => {
        if (!loadingGantData) {
            scrollToDate(new Date(), false);
        }
    }, [loadingGantData]);

    const ganttData = useMemo(() => {
        return data.map(dataItem => {
            const standIns: GanttTimeline[] = [];
            dataItem.standIns.forEach(standIn => {
                const standInDays = getDatesBetween(standIn.start, standIn.end);
                standInDays.forEach(item => {
                    standIns.push({
                        substituteFirstName: standIn.substituteFirstName,
                        substituteLastName: standIn.substituteLastName,
                        substituteId: standIn.substituteId,
                        numberOfDays: standInDays.length,
                        color: getColorByUuid(standIn.substituteId),
                        day: item.toDate(),
                        isStart: item.isSame(standIn.start, 'day'),
                        isEnd: item.isSame(standIn.end, 'day'),
                    });
                });
            });

            return {
                ...dataItem,
                standIns,
            };
        });
    }, [data]);

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <DebouncedInput
                    leftSection={<IconSearch />}
                    placeholder="Search"
                    // value={searchValue}
                    // onChange={event => onSearch(event.target.value)}
                    className="mr-auto w-1/3"
                />
                <Flex gap="8px">
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={() => scrollToDate(new Date())}
                        fullWidth={false}
                        radius="md"
                        className="h-[34px]"
                    >
                        Today
                    </Button>
                    <Button
                        variant="subtle"
                        className="text-primary rounded-md p-1 w-min h-min "
                        // onClick={getUsers}
                    >
                        <IconRefresh />
                    </Button>
                </Flex>
            </div>
            <ScrollArea
                viewportRef={scrollRef}
                classNames={{
                    viewport: 'h-[calc(100vh-300px)]',
                }}
            >
                <LoadingOverlay
                    visible={loadingGantData && data.length === 0}
                />
                <Table className="w-full mb-6">
                    <thead className="sticky top-0 z-20">
                        <tr className="bg-[#F8FAFC]">
                            <th
                                className="sticky left-0 bg-[#F8FAFC]"
                                style={{
                                    height: 59,
                                    padding: 0,
                                }}
                            >
                                <div
                                    className="flex items-center"
                                    style={{
                                        width: 300,
                                        height: '100%',
                                        borderRight: '1px solid #E5E7EB',
                                        borderBottom: '1px solid #E5E7EB',
                                        padding: '8px',
                                    }}
                                >
                                    <Text size="16px" fw={700}>
                                        Full Name
                                    </Text>
                                </div>
                            </th>
                            <th
                                style={{
                                    padding: 0,
                                    width: 56,
                                    borderRight: '1px solid #E5E7EB',
                                    backgroundColor: '#F8FAFC',
                                }}
                            >
                                <div
                                    className="flex items-center"
                                    style={{
                                        width: '100%',
                                        height: '64.44px',
                                        borderBottom: '1px solid #E5E7EB',
                                        padding: '8px',
                                    }}
                                />
                            </th>
                            {days.map(day => (
                                <th
                                    key={day.toISOString()}
                                    className="text-center"
                                    style={{
                                        width: 104,
                                        padding: 0,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            borderRight: '1px solid #E5E7EB',
                                            borderBottom: '1px solid #E5E7EB',
                                            padding: '8px',
                                        }}
                                    >
                                        <Stack
                                            spacing="2px"
                                            align="center"
                                            justify="center"
                                        >
                                            <Flex>
                                                <Text size="16px" fw={700}>
                                                    {day.format('D MMM')}
                                                </Text>
                                            </Flex>

                                            <Text
                                                size="13px"
                                                c="#ACB5BD"
                                                weight="500"
                                            >
                                                {day.format('ddd')}
                                            </Text>
                                        </Stack>
                                    </div>
                                </th>
                            ))}
                            <th
                                style={{
                                    width: 56,
                                    padding: 0,
                                    borderRight: '1px solid #E5E7EB',
                                    backgroundColor: '#F8FAFC',
                                }}
                            >
                                <div
                                    className="flex items-center"
                                    style={{
                                        width: '100%',
                                        height: '64.44px',
                                        borderBottom: '1px solid #E5E7EB',
                                        padding: '8px',
                                    }}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {ganttData.map((ganttRow, index) => (
                            <tr
                                key={ganttRow.primaryUserId}
                                className={`h-[59px] ${
                                    index % 2 === 0
                                        ? 'bg-white'
                                        : 'bg-light-grey'
                                }`}
                            >
                                <td
                                    className={`sticky left-0 z-10 ${
                                        index % 2 === 0
                                            ? 'bg-white'
                                            : 'bg-light-grey'
                                    }`}
                                    style={{
                                        padding: 0,
                                        borderTop: 'none',
                                    }}
                                >
                                    <div
                                        className="flex items-center gap-2 p-4"
                                        style={{
                                            borderRight: '1px solid #E5E7EB',
                                        }}
                                    >
                                        <Text size="16px" weight="400">
                                            {ganttRow.firstName}{' '}
                                            {ganttRow.lastName}
                                        </Text>
                                    </div>
                                </td>
                                {index === 0 && (
                                    <td
                                        rowSpan={ganttData.length}
                                        style={{
                                            textAlign: 'center',
                                            verticalAlign: 'middle',
                                            borderRight: '1px solid #E5E7EB',
                                        }}
                                    >
                                        <Loader />
                                    </td>
                                )}
                                {days.map(day => {
                                    const standIn = ganttRow.standIns.find(
                                        item => {
                                            return day.isSame(item.day, 'day');
                                        },
                                    );

                                    if (!standIn) {
                                        return (
                                            <td
                                                key={day.toISOString()}
                                                style={{
                                                    padding: 0,
                                                    borderTop: 'none',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 104,
                                                        height: 59,
                                                        borderRight:
                                                            '1px solid #E5E7EB',
                                                    }}
                                                />
                                            </td>
                                        );
                                    }

                                    return (
                                        <td
                                            key={day.toISOString()}
                                            style={{
                                                padding: 0,
                                                borderTop: 'none',
                                            }}
                                        >
                                            <div
                                                className="flex items-center w-full overflow-x-visible"
                                                style={{
                                                    width: 104,
                                                    height: 59,
                                                    borderRight:
                                                        '1px solid #E5E7EB',
                                                }}
                                            >
                                                {standIn.isStart && (
                                                    <HoverCard
                                                        position="bottom-start"
                                                        withArrow
                                                        arrowSize={12}
                                                        withinPortal
                                                        arrowOffset={20}
                                                        openDelay={300}
                                                        radius="lg"
                                                    >
                                                        <HoverCard.Target>
                                                            <div
                                                                className="block h-3 relative left-[20px] "
                                                                style={{
                                                                    width: `${
                                                                        104 *
                                                                            standIn.numberOfDays -
                                                                        40
                                                                    }px`,
                                                                    minWidth: `${
                                                                        104 *
                                                                            standIn.numberOfDays -
                                                                        40
                                                                    }px`,
                                                                    maxWidth: `${
                                                                        104 *
                                                                            standIn.numberOfDays -
                                                                        40
                                                                    }px`,
                                                                    borderRadius:
                                                                        '40px',
                                                                    backgroundColor:
                                                                        mantineColorHexCodes[
                                                                            standIn
                                                                                .color
                                                                        ],
                                                                }}
                                                            />
                                                        </HoverCard.Target>
                                                        <HoverCard.Dropdown>
                                                            <Flex direction="column">
                                                                <Flex justify="space-between">
                                                                    <Title
                                                                        order={
                                                                            5
                                                                        }
                                                                    >
                                                                        {
                                                                            standIn.substituteFirstName
                                                                        }{' '}
                                                                        {
                                                                            standIn.substituteLastName
                                                                        }
                                                                    </Title>
                                                                    <Badge
                                                                        color={
                                                                            standIn.color
                                                                        }
                                                                    >
                                                                        Stand-In
                                                                    </Badge>
                                                                </Flex>
                                                                <Flex>
                                                                    <Text
                                                                        color="dimmed"
                                                                        pr="xs"
                                                                        size="sm"
                                                                    >
                                                                        Duration:
                                                                    </Text>
                                                                    <Text size="sm">
                                                                        {
                                                                            standIn.numberOfDays
                                                                        }{' '}
                                                                        day
                                                                        {standIn.numberOfDays >
                                                                        1
                                                                            ? 's'
                                                                            : ''}{' '}
                                                                        (
                                                                        {dayjs(
                                                                            standIn.day,
                                                                        ).format(
                                                                            'D MMM YYYY',
                                                                        )}{' '}
                                                                        -{' '}
                                                                        {dayjs(
                                                                            standIn.day,
                                                                        )
                                                                            .add(
                                                                                standIn.numberOfDays,
                                                                                'days',
                                                                            )
                                                                            .format(
                                                                                'D MMM YYYY',
                                                                            )}
                                                                        )
                                                                    </Text>
                                                                </Flex>
                                                            </Flex>
                                                        </HoverCard.Dropdown>
                                                    </HoverCard>
                                                )}
                                            </div>
                                        </td>
                                    );
                                })}
                                {index === 0 && (
                                    <td
                                        rowSpan={ganttData.length}
                                        style={{
                                            textAlign: 'center',
                                            verticalAlign: 'middle',
                                            borderRight: '1px solid #E5E7EB',
                                        }}
                                    >
                                        <Loader />
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </ScrollArea>
        </div>
    );
}

export default StandInCalendar;
